import React, { useState } from 'react';
import { Loader2, Send, History, Zap, User, CheckCircle2, XCircle, CalendarClock, Clock, Calendar } from 'lucide-react';
import { logProspectAction } from '../services/prospectService';
import { QuickDates } from './ProspectForm';
import {
    STATUTS, STATUT_MAP, STATUTS_CLOS, ACTIONS_SUGGESTIONS, PROCHAINES_ACTIONS_SUGGESTIONS,
    RESULTATS_ACTION, OBJECTIONS, OBJECTION_MAP, formatDate, toInputDate, interetLevel, isWaitingAction
} from '../config/prospects';

const inputCls = 'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition';
const labelCls = 'block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5';

const QUICK_ACTIONS = ['Appel effectué', 'WhatsApp envoyé', 'Email envoyé', 'Démo faite', 'Offre envoyée'];
const QUICK_TIMES = ['09:30', '11:00', '14:30', '16:00'];

const getSmartPlaceholder = (statut) => {
    switch (statut) {
        case 'demo_programmee': return "Faire la démo avec l'équipe…";
        case 'demo_effectuee': return "Envoyer l'offre / devis détaillé…";
        case 'offre_envoyee': return 'Relancer pour retour sur devis…';
        case 'negociation': return 'Finaliser contrat / valider modalité…';
        case 'contacte': return 'Relancer par appel ou WhatsApp…';
        case 'reponse_recue': return 'Répondre aux questions / fixer démo…';
        case 'interesse': return 'Proposer un créneau de démo…';
        default: return 'Relancer…';
    }
};

const isDispatchAction = (actionStr) => {
    if (!actionStr) return false;
    const a = actionStr.toLowerCase();
    const dispatchKeywords = ['email', 'whatsapp', 'message', 'sms', 'devis envoyé', 'offre envoyée', 'brochure', 'catalogue', 'présentation'];
    if (a.includes('appel') || a.includes('reçu') || a.includes('réponse') || a.includes('visite') || a.includes('démo') || a.includes('réunion') || a.includes('entretien')) {
        return false;
    }
    return dispatchKeywords.some(k => a.includes(k));
};

/**
 * "Nouvelle action" logger avec :
 * 1. Résultat / Issue de l'action (Outcome)
 * 2. Heure de la prochaine action (Optionnel)
 * 3. Date de réalisation de l'action elle-même (Historique passé)
 * 4. Niveau d'intérêt direct (%)
 * 5. Logique conditionnelle selon le statut
 */
const ProspectActivity = ({ prospect, responsables = [], onUpdated, onViewHistory }) => {
    const isInitiallyClosed = STATUTS_CLOS.includes(prospect.statut);
    const now = new Date();

    const [form, setForm] = useState({
        action: '',
        resultat: '',
        objection: prospect.objection || '',
        note: '',
        statut: prospect.statut,
        interet: prospect.interet ?? (STATUT_MAP[prospect.statut]?.interet ?? 0),
        responsable: prospect.responsable || '',

        // Date de réalisation de l'action
        dateActionChoice: 'now', // 'now' | 'yesterday' | 'custom'
        customDateAction: toInputDate(now),
        customHeureAction: now.toTimeString().slice(0, 5),

        // Prochaine action
        hasProchaineAction: !isInitiallyClosed && Boolean(prospect.prochaineAction || prospect.dateProchaineAction),
        prochaineAction: isInitiallyClosed ? '' : (prospect.prochaineAction || ''),
        dateProchaineAction: isInitiallyClosed ? '' : (prospect.dateProchaineAction ? toInputDate(prospect.dateProchaineAction) : ''),
        heureProchaineAction: isInitiallyClosed ? '' : (prospect.heureProchaineAction || '')
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showManualObjection, setShowManualObjection] = useState(false);

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const handleStatutChange = (newStatut) => {
        const isClosed = STATUTS_CLOS.includes(newStatut);
        const defaultInteret = STATUT_MAP[newStatut]?.interet ?? 0;
        setForm(prev => ({
            ...prev,
            statut: newStatut,
            interet: defaultInteret,
            hasProchaineAction: isClosed ? false : prev.hasProchaineAction,
            prochaineAction: isClosed ? '' : prev.prochaineAction,
            dateProchaineAction: isClosed ? '' : prev.dateProchaineAction,
            heureProchaineAction: isClosed ? '' : prev.heureProchaineAction
        }));
    };

    const handlePickAction = (act) => {
        if (isDispatchAction(act)) {
            setForm(prev => ({
                ...prev,
                action: act,
                resultat: '',
                objection: '',
                hasProchaineAction: true,
                prochaineAction: prev.prochaineAction || 'Relancer par téléphone'
            }));
            setShowManualObjection(false);
        } else {
            setForm(prev => ({ ...prev, action: act }));
        }
    };

    const handleActionChange = (val) => {
        if (isDispatchAction(val) && (form.resultat || form.objection)) {
            setForm(prev => ({ ...prev, action: val, resultat: '', objection: '' }));
            setShowManualObjection(false);
        } else {
            set('action', val);
        }
    };

    const handlePickOutcome = (out) => {
        // Toggle if clicked again
        if (form.resultat === out.label) {
            set('resultat', '');
            setShowManualObjection(false);
            return;
        }

        // Smart suggestions based on the outcome
        if (out.value === 'pas_de_reponse') {
            handleStatutChange('pas_de_reponse');
            setShowManualObjection(false);
            setForm(prev => ({
                ...prev,
                resultat: out.label,
                objection: '',
                hasProchaineAction: true,
                prochaineAction: 'Relancer par téléphone'
            }));
        } else if (out.value === 'occupe') {
            setShowManualObjection(false);
            setForm(prev => ({
                ...prev,
                resultat: out.label,
                objection: '',
                hasProchaineAction: true,
                prochaineAction: 'Rappeler plus tard'
            }));
        } else if (out.value === 'rdv_fixe') {
            handleStatutChange('demo_programmee');
            setShowManualObjection(false);
            setForm(prev => ({
                ...prev,
                resultat: out.label,
                hasProchaineAction: true,
                prochaineAction: "Faire la démo avec l'équipe"
            }));
        } else if (out.value === 'refus') {
            handleStatutChange('pas_interesse');
            setShowManualObjection(true);
            setForm(prev => ({
                ...prev,
                resultat: out.label,
                hasProchaineAction: false,
                prochaineAction: '',
                dateProchaineAction: '',
                heureProchaineAction: ''
            }));
        } else if (out.value === 'devis_demande') {
            handleStatutChange('offre_envoyee');
            setShowManualObjection(false);
            setForm(prev => ({
                ...prev,
                resultat: out.label,
                hasProchaineAction: true,
                prochaineAction: 'Envoyer devis / offre chiffrée'
            }));
        } else if (out.value === 'positif') {
            if (form.statut === 'a_contacter' || form.statut === 'contacte') {
                handleStatutChange('interesse');
            }
            setShowManualObjection(false);
            setForm(prev => ({
                ...prev,
                resultat: out.label
            }));
        } else {
            set('resultat', out.label);
        }
    };

    const isClosed = STATUTS_CLOS.includes(form.statut);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.action.trim()) {
            setError("Indiquez l'action effectuée.");
            return;
        }
        setSaving(true);
        setError('');
        try {
            // Calculate action realization date
            let actionDate = new Date();
            if (form.dateActionChoice === 'yesterday') {
                actionDate.setDate(actionDate.getDate() - 1);
            } else if (form.dateActionChoice === 'custom' && form.customDateAction) {
                const h = form.customHeureAction || '12:00';
                const parsed = new Date(`${form.customDateAction}T${h}:00`);
                if (!isNaN(parsed.getTime())) actionDate = parsed;
            }

            const payload = {
                action: form.action.trim(),
                resultat: form.resultat.trim(),
                objection: form.objection.trim(),
                dateAction: actionDate.toISOString(),
                note: form.note.trim(),
                statut: form.statut,
                interet: Number(form.interet) || 0,
                responsable: form.responsable.trim(),
                prochaineAction: (!isClosed && form.hasProchaineAction) ? form.prochaineAction.trim() : '',
                dateProchaineAction: (!isClosed && form.hasProchaineAction && form.dateProchaineAction) ? form.dateProchaineAction : null,
                heureProchaineAction: (!isClosed && form.hasProchaineAction && form.heureProchaineAction) ? form.heureProchaineAction.trim() : ''
            };

            const updated = await logProspectAction(prospect._id, payload);
            onUpdated?.(updated, 'Action enregistrée');
            setShowManualObjection(false);
            setForm({
                action: '',
                resultat: '',
                objection: updated.objection || '',
                note: '',
                statut: updated.statut,
                interet: updated.interet ?? 0,
                responsable: updated.responsable || '',
                dateActionChoice: 'now',
                customDateAction: toInputDate(new Date()),
                customHeureAction: new Date().toTimeString().slice(0, 5),
                hasProchaineAction: !STATUTS_CLOS.includes(updated.statut) && Boolean(updated.prochaineAction),
                prochaineAction: updated.prochaineAction || '',
                dateProchaineAction: updated.dateProchaineAction ? toInputDate(updated.dateProchaineAction) : '',
                heureProchaineAction: updated.heureProchaineAction || ''
            });
        } catch (err) {
            setError(err.response?.data?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const historique = prospect.historique || [];

    const isDispatch = isDispatchAction(form.action);
    const isNoResponse = form.resultat === 'Ne répond pas' || form.resultat === 'Occupé / Rappeler';
    const isRefus = form.resultat === 'Pas intéressé / Refus' || form.statut === 'pas_interesse';
    const isWon = form.statut === 'gagne' || prospect.statut === 'gagne';

    const availableOutcomes = isWon
        ? RESULTATS_ACTION.filter(r => r.value !== 'refus')
        : RESULTATS_ACTION;

    const isObjectionVisible = !isDispatch && !isNoResponse && !isWon && (isRefus || showManualObjection || Boolean(form.objection));

    return (
        <div className="space-y-6">
            {/* Current situation recap */}
            <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Dernière action</div>
                    <div className="text-xs font-bold text-slate-900 truncate" title={prospect.derniereAction}>{prospect.derniereAction || '—'}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">{formatDate(prospect.dateDerniereAction, true)}</div>
                </div>
                {prospect.statut === 'gagne' ? (
                    <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-lg">
                        <div className="text-[9px] font-black uppercase tracking-widest text-emerald-600 mb-1">Statut commercial</div>
                        <div className="text-xs font-bold text-emerald-800 flex items-center gap-1">
                            <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                            Client converti 🎉
                        </div>
                        <div className="text-[10px] font-medium text-emerald-600 mt-0.5">Dossier clos avec succès</div>
                    </div>
                ) : prospect.statut === 'pas_interesse' ? (
                    <div className="p-3 bg-slate-100/70 border border-slate-200 rounded-lg">
                        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Statut commercial</div>
                        <div className="text-xs font-bold text-slate-700 flex items-center gap-1">
                            <XCircle size={13} className="text-slate-400 shrink-0" />
                            Sans suite (Refus)
                        </div>
                        <div className="text-[10px] font-medium text-slate-500 mt-0.5">Archivé · Aucune relance</div>
                    </div>
                ) : isWaitingAction(prospect.prochaineAction) ? (
                    <div className="p-3 bg-indigo-50/60 border border-indigo-200 rounded-lg">
                        <div className="text-[9px] font-black uppercase tracking-widest text-indigo-500 mb-1">En attente</div>
                        <div className="text-xs font-bold text-indigo-900 truncate" title={prospect.prochaineAction}>
                            ⏳ {prospect.prochaineAction}
                        </div>
                        <div className="text-[10px] font-medium text-indigo-600 mt-0.5">
                            {prospect.dateProchaineAction ? `Relance : ${formatDate(prospect.dateProchaineAction)}` : "En attente de retour du client"}
                        </div>
                    </div>
                ) : (
                    <div className="p-3 bg-blue-50/60 border border-blue-200 rounded-lg">
                        <div className="text-[9px] font-black uppercase tracking-widest text-blue-500 mb-1">Prochaine action</div>
                        <div className="text-xs font-bold text-slate-900 truncate" title={prospect.prochaineAction}>{prospect.prochaineAction || '—'}</div>
                        <div className="text-[10px] font-mono text-blue-500 mt-0.5">
                            {formatDate(prospect.dateProchaineAction, true)}
                            {prospect.heureProchaineAction ? ` à ${prospect.heureProchaineAction}` : ''}
                        </div>
                    </div>
                )}
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Statut actuel</div>
                    <div className="text-xs font-bold text-slate-900">{STATUT_MAP[prospect.statut]?.emoji} {STATUT_MAP[prospect.statut]?.label}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">Intérêt {prospect.interet || 0}%</div>
                </div>
            </div>

            {/* New action logger */}
            <form onSubmit={handleSubmit} className="p-5 border border-slate-200 rounded-xl space-y-4 bg-white shadow-sm">
                <div className="flex items-center justify-between pb-1 border-b border-slate-100">
                    <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.15em] text-slate-900">
                        <Zap size={14} className="text-amber-500" />
                        Nouvelle action
                    </div>

                    {/* Date de réalisation de l'action (Feature 5) */}
                    <div className="flex items-center gap-1">
                        <span className="text-[10px] font-bold text-slate-400 mr-1 hidden sm:inline">Faite :</span>
                        <button
                            type="button"
                            onClick={() => set('dateActionChoice', 'now')}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${
                                form.dateActionChoice === 'now' ? 'bg-slate-900 text-white shadow-xs' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            Maintenant
                        </button>
                        <button
                            type="button"
                            onClick={() => set('dateActionChoice', 'yesterday')}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${
                                form.dateActionChoice === 'yesterday' ? 'bg-slate-900 text-white shadow-xs' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            Hier
                        </button>
                        <button
                            type="button"
                            onClick={() => set('dateActionChoice', 'custom')}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${
                                form.dateActionChoice === 'custom' ? 'bg-slate-900 text-white shadow-xs' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            Autre date…
                        </button>
                    </div>
                </div>

                {/* Champ date personnalisée si sélectionné */}
                {form.dateActionChoice === 'custom' && (
                    <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg grid grid-cols-2 gap-2 text-xs">
                        <div>
                            <label className="block text-[9px] font-bold text-slate-500 mb-1">Date de l'action</label>
                            <input
                                type="date"
                                className={inputCls + ' text-xs py-1.5'}
                                value={form.customDateAction}
                                max={toInputDate(new Date())}
                                onChange={e => set('customDateAction', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[9px] font-bold text-slate-500 mb-1">Heure approximative</label>
                            <input
                                type="time"
                                className={inputCls + ' text-xs py-1.5'}
                                value={form.customHeureAction}
                                onChange={e => set('customHeureAction', e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {/* 1. Action effectuée */}
                <div>
                    <label className={labelCls}>Qu'avez-vous fait ?</label>
                    <input
                        autoFocus
                        list="activity-actions"
                        className={inputCls}
                        value={form.action}
                        onChange={e => handleActionChange(e.target.value)}
                        placeholder="Ex : Appel effectué"
                    />
                    <datalist id="activity-actions">
                        {ACTIONS_SUGGESTIONS.map(a => <option key={a} value={a} />)}
                    </datalist>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {QUICK_ACTIONS.map(a => (
                            <button
                                key={a}
                                type="button"
                                onClick={() => handlePickAction(a)}
                                className={`px-2 py-1 text-[10px] font-bold rounded-md transition ${
                                    form.action === a ? 'bg-slate-900 text-white shadow-xs' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                {a}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 2. Résultat / Issue de l'action : Affiché seulement si action d'échange direct */}
                {isDispatch ? (
                    <div className="flex items-center gap-2.5 p-3 bg-blue-50/70 border border-blue-200/60 rounded-xl text-xs text-blue-900">
                        <span className="text-lg">✉️</span>
                        <div>
                            <div className="font-bold">Message ou document envoyé</div>
                            <div className="text-blue-600 text-[11px] mt-0.5">En attente de retour. Prévoyez la date de relance ci-dessous.</div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className={labelCls}>Résultat / Issue de l'action</label>
                            {form.resultat && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        set('resultat', '');
                                        setShowManualObjection(false);
                                    }}
                                    className="text-[10px] text-slate-400 hover:text-slate-600 font-bold"
                                >
                                    Effacer
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                            {availableOutcomes.map(r => {
                                const isSelected = form.resultat === r.label;
                                return (
                                    <button
                                        key={r.value}
                                        type="button"
                                        onClick={() => handlePickOutcome(r)}
                                        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg border text-left transition ${
                                            isSelected
                                                ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span>{r.emoji}</span>
                                        <span className="truncate">{r.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Lien discret pour ajouter une objection si résultat positif/en attente */}
                        {!isObjectionVisible && !isNoResponse && !isWon && (
                            <div className="flex justify-end pt-1.5">
                                <button
                                    type="button"
                                    onClick={() => setShowManualObjection(true)}
                                    className="text-[10px] font-bold text-slate-400 hover:text-red-600 transition inline-flex items-center gap-1"
                                >
                                    <span>🛑</span>
                                    <span>Signaler un frein / une réserve</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* 3. Objection principale / Frein rencontré : Affiché UNIQUEMENT si refus, ou déclenché par l'utilisateur */}
                {isObjectionVisible && (
                    <div className="p-3 bg-red-50/50 border border-red-200/80 rounded-xl space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] font-black uppercase tracking-widest text-red-700 flex items-center gap-1.5">
                                <span>🛑</span>
                                <span>
                                    {isRefus ? "Motif principal du refus (Recommandé)" : "Objection principale / Frein rencontré (Optionnel)"}
                                </span>
                            </label>
                            <button
                                type="button"
                                onClick={() => {
                                    set('objection', '');
                                    setShowManualObjection(false);
                                }}
                                className="text-[10px] text-red-500 hover:text-red-700 font-bold"
                            >
                                Masquer
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {OBJECTIONS.map(obj => {
                                const isSelected = form.objection === obj.label;
                                return (
                                    <button
                                        key={obj.value}
                                        type="button"
                                        onClick={() => set('objection', isSelected ? '' : obj.label)}
                                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-lg border transition ${
                                            isSelected
                                                ? 'bg-red-600 text-white border-red-600 shadow-xs'
                                                : 'bg-white text-slate-700 border-red-200 hover:bg-red-50'
                                        }`}
                                    >
                                        <span>{obj.emoji}</span>
                                        <span>{obj.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {form.objection && (
                            <div className="pt-1">
                                <input
                                    type="text"
                                    className={inputCls + ' text-xs py-1.5 bg-white border-red-200 focus:border-red-400'}
                                    value={form.objection}
                                    onChange={e => set('objection', e.target.value)}
                                    placeholder="Précisez ou personnalisez l'objection..."
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* 4. Compte-rendu & détails complémentaires */}
                <div>
                    <label className={labelCls}>Compte-rendu & détails complémentaires (Optionnel)</label>
                    <textarea
                        rows={2}
                        className={inputCls + ' resize-y'}
                        value={form.note}
                        onChange={e => set('note', e.target.value)}
                        placeholder="Ex : Le directeur veut comparer avec le logiciel actuel avant d'accorder une démo..."
                    />
                </div>

                {/* 4. Statut & Responsable */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Nouveau statut</label>
                        <select className={inputCls} value={form.statut} onChange={e => handleStatutChange(e.target.value)}>
                            {STATUTS.map(s => <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Responsable de l'action</label>
                        <input
                            list="activity-responsables"
                            className={inputCls}
                            value={form.responsable}
                            onChange={e => set('responsable', e.target.value)}
                            placeholder="Ex : Brahim, Ahmed..."
                        />
                        {responsables && responsables.length > 0 && (
                            <datalist id="activity-responsables">
                                {responsables.map(r => <option key={r} value={r} />)}
                            </datalist>
                        )}
                    </div>
                </div>

                {/* 5. Niveau d'intérêt */}
                <div className="p-3 bg-slate-50/70 border border-slate-200 rounded-xl space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Niveau d'intérêt</label>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black font-mono text-slate-900">{form.interet}%</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${interetLevel(form.interet).text}`}>
                                {interetLevel(form.interet).label}
                            </span>
                        </div>
                    </div>
                    <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={form.interet}
                        onChange={e => set('interet', Number(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex items-center justify-between gap-1 pt-0.5">
                        {[0, 20, 50, 70, 90, 100].map(val => (
                            <button
                                key={val}
                                type="button"
                                onClick={() => set('interet', val)}
                                className={`px-2 py-0.5 text-[9px] font-bold font-mono rounded transition ${
                                    form.interet === val ? 'bg-slate-900 text-white shadow-xs' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                                }`}
                            >
                                {val}%
                            </button>
                        ))}
                    </div>
                </div>

                {/* 6. Prochaine action (conditionnelle + Heure optionnelle - Feature 2) */}
                {isClosed ? (
                    <div className={`p-3.5 rounded-xl border flex items-center gap-3 ${
                        form.statut === 'gagne'
                            ? 'bg-emerald-50/70 border-emerald-200 text-emerald-800'
                            : 'bg-red-50/70 border-red-200 text-red-800'
                    }`}>
                        {form.statut === 'gagne' ? (
                            <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
                        ) : (
                            <XCircle size={20} className="text-red-500 shrink-0" />
                        )}
                        <div className="min-w-0">
                            <div className="text-xs font-bold">
                                {form.statut === 'gagne' ? '🎉 Client gagné ! Contrat conclu' : 'Dossier classé sans suite (Pas intéressé)'}
                            </div>
                            <div className="text-[11px] opacity-80 mt-0.5">
                                {form.statut === 'gagne'
                                    ? 'Le prospect est désormais client. Aucune relance commerciale nécessaire.'
                                    : 'Le prospect a refusé. Aucune relance commerciale planifiée.'}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={form.hasProchaineAction}
                                    onChange={e => {
                                        const checked = e.target.checked;
                                        set('hasProchaineAction', checked);
                                        if (!checked) {
                                            set('prochaineAction', '');
                                            set('dateProchaineAction', '');
                                            set('heureProchaineAction', '');
                                        }
                                    }}
                                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500/20 border-slate-300 accent-blue-600 cursor-pointer"
                                />
                                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                    <CalendarClock size={13} className="text-blue-600" />
                                    Planifier une prochaine action / relance
                                </span>
                            </label>
                            {!form.hasProchaineAction && (
                                <span className="text-[10px] text-slate-400 font-medium italic">
                                    Aucune relance prévue
                                </span>
                            )}
                        </div>

                        {form.hasProchaineAction && (
                            <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-blue-200">
                                <div>
                                    <label className={labelCls}>Action à faire</label>
                                    <input
                                        list="activity-next-actions"
                                        className={inputCls}
                                        value={form.prochaineAction}
                                        onChange={e => set('prochaineAction', e.target.value)}
                                        placeholder={getSmartPlaceholder(form.statut)}
                                    />
                                    <datalist id="activity-next-actions">
                                        {PROCHAINES_ACTIONS_SUGGESTIONS.map(a => <option key={a} value={a} />)}
                                    </datalist>
                                </div>

                                {/* Date + Heure (Optionnelle !) */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="sm:col-span-2">
                                        <label className={labelCls}>Pour quand ?</label>
                                        <input
                                            type="date"
                                            className={inputCls}
                                            value={form.dateProchaineAction}
                                            min={toInputDate(new Date())}
                                            onChange={e => set('dateProchaineAction', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Heure</label>
                                            <span className="text-[9px] text-slate-400 italic">Optionnel</span>
                                        </div>
                                        <input
                                            type="time"
                                            className={inputCls}
                                            value={form.heureProchaineAction}
                                            onChange={e => set('heureProchaineAction', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Quick times if relevant */}
                                <div className="flex flex-wrap items-center justify-between gap-1.5">
                                    <QuickDates onPick={v => set('dateProchaineAction', v)} />
                                    <div className="flex items-center gap-1">
                                        {QUICK_TIMES.map(h => (
                                            <button
                                                key={h}
                                                type="button"
                                                onClick={() => set('heureProchaineAction', h)}
                                                className={`px-1.5 py-0.5 text-[9px] font-mono font-bold rounded transition ${
                                                    form.heureProchaineAction === h
                                                        ? 'bg-slate-900 text-white'
                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                            >
                                                {h}
                                            </button>
                                        ))}
                                        {form.heureProchaineAction && (
                                            <button
                                                type="button"
                                                onClick={() => set('heureProchaineAction', '')}
                                                className="text-[9px] font-bold text-red-500 hover:text-red-700 ml-1"
                                                title="Retirer l'heure"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {error && (
                    <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-xs font-bold rounded-lg">{error}</div>
                )}

                <div className="flex items-center justify-between pt-2">
                    {onViewHistory ? (
                        <button
                            type="button"
                            onClick={onViewHistory}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition"
                        >
                            <History size={14} className="text-blue-600" />
                            Voir l'historique ({historique.length})
                        </button>
                    ) : <span />}

                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 disabled:opacity-60 transition shadow-sm"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        Enregistrer l'action
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProspectActivity;
